<template>
	<fieldset class="c-business-information flex flex-col b7:col-start-2">
		<!-- Reduce padding bottom a little to compensate for extra space in radio button labels -->
		<div class="field pb-1">
			<label class="flex flex-col">
				<div class="flex items-center">
					<strong>
						Organization Type
					</strong>
					<a href="/support/192/account-organizations/plans-billing/changing-your-billing-information" target="_blank" class="support-icon-link ml-1">
						<Inline svg="support" />
					</a>
				</div>
				<ElRadioGroup
					v-model="internalOrganizationType"
					class="c-radio-fancy mt-2 flex flex-wrap gap-x-3"
					@change="emitOrganizationType"
				>
					<ElRadioButton label="Business">Business</ElRadioButton>
					<ElRadioButton label="Individual">Personal</ElRadioButton>
				</ElRadioGroup>
			</label>
		</div>
		<TransitionGroup name="slide" tag="div" class="flex flex-col justify-between">
			<template v-if="internalOrganizationType === 'Business'">
				<CValidatedInput
					key="BusinessName"
					:value="business || ''"
					vid="BusinessName"
					label="Business Name"
					name="Business Name"
					type="text"
					rules="required"
					@input="updateBusiness"
				/>
			</template>
			<CBusinessTaxFields
				v-if="showTaxFields"
				key="TaxFields"
				:organization-type="internalOrganizationType"
				:tax-ids="taxIds"
				:country-code="countryCode"
				:state="state"
				:postal-code="postalCode"
				:tax-id-invalid="taxIdInvalid"
				:purchase-order-number="purchaseOrderNumber"
				:is-v-a-t-registration-opt-out="isVATRegistrationOptOut"
				@reset-tax-id-invalid="$emit('reset-tax-id-invalid')"
				@update:tax-id="$emit('update:tax-id', $event)"
				@update:purchase-order-number="$emit('update:purchase-order-number', $event)"
				@update:is-vat-registration-opt-out="$emit('update:is-vat-registration-opt-out', $event)"
			/>
		</TransitionGroup>
	</fieldset>
</template>

<script lang="ts">
	import CValidatedInput from 'src/components/form-controls/ValidatedInput.vue';
	import { RadioGroup, RadioButton } from '@cognitoforms/element-ui';
	import type { TaxId } from '@cognitoforms/types/server-types/plans/model/tax-id.ts';
	import type { PropType } from 'vue';
	import { defineComponent, ref, watch } from 'vue';
	import CBusinessTaxFields from './BusinessTaxFields.vue';

	export default defineComponent({
		name: 'CBusinessInformation',
		components: {
			CValidatedInput,
			CBusinessTaxFields,
			ElRadioGroup: RadioGroup,
			ElRadioButton: RadioButton
		},
		props: {
			organizationType: {
				type: String,
				validator: (val: string) => ['Business', 'Individual'].includes(val),
				default: 'Business'
			},
			business: {
				type: String,
				default: ''
			},
			taxIds: {
				type: Array as PropType<TaxId[]>,
				required: false,
				default: () => []
			},
			countryCode: {
				type: String,
				required: true
			},
			state: {
				type: String,
				default: null
			},
			postalCode: {
				type: String,
				default: null
			},
			taxIdInvalid: {
				type: Boolean,
				required: true
			},
			purchaseOrderNumber: {
				type: String,
				default: ''
			},
			paymentMethod: {
				type: String,
				default: ''
			},
			isVATRegistrationOptOut: {
				type: Boolean,
				default: false
			},
			showTaxFields: {
				type: Boolean,
				default: true
			}
		},
		emits: [
			'update:organization-type',
			'update:business',
			'update:tax-id',
			'reset-tax-id-invalid',
			'update:purchase-order-number',
			'update:is-vat-registration-opt-out'
		],
		setup(props, { emit }) {
			const internalOrganizationType = ref(props.organizationType);

			const emitOrganizationType = (val: string) => {
				// Clear tax IDs and VAT opt-out when switching to Individual
				if (val === 'Individual') {
					emit('update:tax-id', []);
					emit('update:is-vat-registration-opt-out', false);
					emit('update:business', '');
				}
				emit('update:organization-type', val);
			};

			watch(() => props.organizationType, val => {
				internalOrganizationType.value = val;
				if (!val)
					emitOrganizationType('Business');
			}, { immediate: true });

			return {
				internalOrganizationType,
				emitOrganizationType,
				updateBusiness: (val: string) => {
					emit('update:business', val);
				}
			};
		}
	});
</script>

<style lang="scss">
.c-business-information {
	// Ensure the component doesn't cause viewport recalculation/thrashing causing a shortened viewport
	contain: layout style;
}
</style>
